import React from "react";
import {
    Flex, Stack, Box, Text, Badge, Button, IconButton, Icon, Input, Select, Checkbox,
    FormControl, FormLabel, FormHelperText, useColorModeValue,
} from "@chakra-ui/react";
import { MdClose, MdSave } from "react-icons/md";
import { ROLE_OPTIONS, LANGUAGE_OPTIONS } from "./legalFormStyles";

/**
 * Renders the top header bar of the form drawer with title, version badge, and action buttons.
 * @param {object} props
 * @param {boolean} props.isEdit - Whether in edit mode
 * @param {object|null} props.doc - Document being edited
 * @param {boolean} props.loading - Whether form is submitting
 * @param {function} props.onClose - Close callback
 * @param {function} props.onSubmit - Submit callback
 * @returns {React.ReactElement}
 */
function FormHeader({ isEdit, doc, loading, onClose, onSubmit }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex justify="space-between" align="center" px="24px" py="12px"
            borderBottom="1px solid" borderColor="whiteAlpha.200">
            <Stack direction="row" spacing="16px" align="center">
                <Text color={titleColor} fontWeight="700" fontSize="1.1rem">
                    {isEdit ? "New Version" : "New Legal Document"}
                </Text>
                {isEdit && (
                    <Badge bg="rgba(255,152,0,0.15)" color="#ff9800" borderRadius="full" px="8px"
                        fontSize="0.7rem" fontWeight="700">FROM v{doc.version}</Badge>
                )}
            </Stack>
            <Stack direction="row" spacing="8px" align="center">
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button colorScheme="green" onClick={onSubmit} isLoading={loading} leftIcon={<Icon as={MdSave} />}>
                    {isEdit ? "Create Version" : "Create"}
                </Button>
                <IconButton aria-label="Fermer" variant="ghost" icon={<Icon as={MdClose} />} onClick={onClose} />
            </Stack>
        </Flex>
    );
}

/**
 * Warning banner displayed in edit mode about version creation side effects.
 * @param {object} props
 * @param {string} props.version - Current document version number
 * @returns {React.ReactElement}
 */
function EditWarningBanner({ version }) {
    return (
        <Box p="12px" borderRadius="8px" bg="rgba(255,152,0,0.08)" border="1px solid" borderColor="rgba(255,152,0,0.2)">
            <Text color="rgba(255,152,0,0.95)" fontSize="0.78rem">
                This will create a new version of the document. The previous version (v{version}) will
                remain active until the effective date, or be deactivated immediately if no date is set.
            </Text>
        </Box>
    );
}

/**
 * Renders the metadata fields row: type selector and version input.
 * @param {object} props
 * @param {object} props.form - Current form state
 * @param {boolean} props.isEdit - Whether in edit mode
 * @param {Array} props.types - Available document types
 * @param {function} props.onChange - Field change handler (field, value)
 * @returns {React.ReactElement}
 */
function TypeVersionFields({ form, isEdit, types, onChange }) {
    return (
        <Stack direction="row" spacing="16px">
            <FormControl>
                <FormLabel fontSize="0.8rem">Type</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.type} isDisabled={isEdit}
                    placeholder="—" onChange={(e) => onChange("type", e.target.value)}>
                    {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
            </FormControl>
            <FormControl>
                <FormLabel fontSize="0.8rem">Version</FormLabel>
                <Input size="sm" borderRadius="8px" value={form.version}
                    placeholder={isEdit ? "Auto-increment" : "1.0"}
                    onChange={(e) => onChange("version", e.target.value)} />
                {isEdit && <FormHelperText fontSize="0.7rem">Leave empty for auto-increment</FormHelperText>}
            </FormControl>
        </Stack>
    );
}

/**
 * Renders language and target role select fields.
 * @param {object} props
 * @param {object} props.form - Current form state
 * @param {function} props.onChange - Field change handler (field, value)
 * @returns {React.ReactElement}
 */
function LanguageRoleFields({ form, onChange }) {
    return (
        <Stack direction="row" spacing="16px">
            <FormControl>
                <FormLabel fontSize="0.8rem">Language</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.language}
                    onChange={(e) => onChange("language", e.target.value)}>
                    {LANGUAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
            </FormControl>
            <FormControl>
                <FormLabel fontSize="0.8rem">Target Role</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.target_role}
                    onChange={(e) => onChange("target_role", e.target.value)}>
                    {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
            </FormControl>
        </Stack>
    );
}

/**
 * Renders platforms text field and effective date picker.
 * @param {object} props
 * @param {object} props.form - Current form state
 * @param {function} props.onChange - Field change handler (field, value)
 * @returns {React.ReactElement}
 */
function PlatformDateFields({ form, onChange }) {
    return (
        <Stack direction="row" spacing="16px">
            <FormControl>
                <FormLabel fontSize="0.8rem">Platforms (comma-separated)</FormLabel>
                <Input size="sm" borderRadius="8px" value={form.platforms} placeholder="all, web, ios, android"
                    onChange={(e) => onChange("platforms", e.target.value)} />
            </FormControl>
            <FormControl>
                <FormLabel fontSize="0.8rem">Effective Date</FormLabel>
                <Input size="sm" borderRadius="8px" type="date" value={form.effective_date}
                    onChange={(e) => onChange("effective_date", e.target.value)} />
            </FormControl>
        </Stack>
    );
}

/**
 * Warning banner reflecting the notification impact of a legal-document edit.
 * @param {object} props
 * @param {boolean} props.willNotify - Whether users will be notified / re-consented
 * @returns {React.ReactElement}
 */
function ChangeTypeWarning({ willNotify }) {
    const palette = willNotify
        ? { bg: "rgba(255,152,0,0.08)", border: "rgba(255,152,0,0.25)", color: "rgba(255,152,0,0.95)" }
        : { bg: "rgba(76,175,80,0.08)", border: "rgba(76,175,80,0.25)", color: "rgba(76,175,80,0.95)" };
    const text = willNotify
        ? "Modification matérielle ou notification activée : une notification (email + push) sera envoyée à tous les utilisateurs concernés et le re-consentement sera exigé au prochain login."
        : "Activation silencieuse : consentements préservés, aucune notification (corrections de pure forme).";
    return (
        <Box p="12px" borderRadius="8px" bg={palette.bg} border="1px solid" borderColor={palette.border}>
            <Text color={palette.color} fontSize="0.78rem">{text}</Text>
        </Box>
    );
}

/**
 * Renders the change-type select, the notify-users checkbox and a contextual
 * warning. Shown only in edit mode (these fields drive update semantics).
 * @param {object} props
 * @param {object} props.form - Current form state (change_type, notify_users)
 * @param {function} props.onChange - Field change handler (field, value)
 * @returns {React.ReactElement}
 */
function ChangeTypeFields({ form, onChange }) {
    const willNotify = form.change_type === "material" || form.notify_users;
    return (
        <Stack spacing="12px">
            <Stack direction="row" spacing="16px" align="flex-end">
                <FormControl>
                    <FormLabel fontSize="0.8rem">Type de modification</FormLabel>
                    <Select size="sm" borderRadius="8px" value={form.change_type}
                        onChange={(e) => onChange("change_type", e.target.value)}>
                        <option value="material">Matérielle</option>
                        <option value="cosmetic">Cosmétique</option>
                    </Select>
                </FormControl>
                <Checkbox colorScheme="brand" whiteSpace="nowrap" isChecked={form.notify_users}
                    onChange={(e) => onChange("notify_users", e.target.checked)}>
                    Notifier les utilisateurs
                </Checkbox>
            </Stack>
            <ChangeTypeWarning willNotify={willNotify} />
        </Stack>
    );
}

export {
    FormHeader, EditWarningBanner, TypeVersionFields, LanguageRoleFields,
    PlatformDateFields, ChangeTypeFields
};
